import React from 'react';
import { AccordionItem, AccordionContent } from '@/components/ui/accordion';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import chevronDownIcon from '@/assets/icons/chevron-down.svg';
import expenseIcon from '@/assets/icons/expense.svg';
import { useNavigate, useParams } from 'react-router-dom';

interface ECItemAccordionProps {
  ec: any;
  graphViewMode: string;
  open: boolean;
  onToggle: () => void;
}

const ECItemAccordion = ({ ec, graphViewMode, open, onToggle }: ECItemAccordionProps) => {
  const navigate = useNavigate();
  const { budgetId } = useParams();

  // Calculate chart data for this specific expense category
  const ecmevRemaining = ec.ecmev || 0;
  const ecmevUsed = ec.ecmev_used || 0;
  const ecmevTotal = ecmevRemaining + ecmevUsed;

  const ecmevChartData = [
    {
      key: 'remaining',
      value: ecmevTotal === 0 ? 100 : Math.round((ecmevRemaining / ecmevTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'spent',
      value: ecmevTotal === 0 ? 0 : Math.round((ecmevUsed / ecmevTotal) * 100),
      fill: '#FF2D55'
    }
  ];

  const ecmevChartConfig = {
    remaining: { label: 'Remaining Budget', color: '#32ADE6' },
    spent: { label: 'Budget Spent', color: '#FF2D55' }
  };

  return (
    <AccordionItem
      value={ec._id}
      className="bg-white md:bg-transparent border rounded-[8px] min-h-[88px] transition-all duration-300"
    >
      <div className="flex select-none py-0 pr-3 min-w-0">
        <div className="shrink-0 min-w-[40px] px-5 py-6 flex items-center justify-center">
          <img className="w-[40px] h-[40px] shrink-0" src={expenseIcon} alt="expense-icon" />
        </div>
        <div className="flex-1 min-w-0 flex justify-center flex-col gap-[0.4rem] border-l p-6 py-4 text-left">
          <button
            title={ec.name || '-'}
            onClick={() => {
              navigate(`/budgets/${budgetId}/expense-categories/${ec._id}`, {
                state: { ec }
              });
            }}
            className="font-bold text-sm md:text-base leading-[110%] capitalize truncate mb-[0.1rem] cursor-pointer text-left w-full min-w-0 block"
          >
            {ec.name || '-'}
          </button>
          <span className="text-xs text-secondary leading-[100%]">
            Remaining ECMEV €{ecmevRemaining?.toLocaleString() || '0'}
          </span>
        </div>

        <div className="shrink-0 flex items-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={`w-[26px] h-[26px] flex items-center justify-center border rounded-full bg-lightPurple-200 ml-2 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-label={open ? 'Collapse' : 'Expand'}
            aria-expanded={open}
          >
            <img src={chevronDownIcon} alt="toggle" />
          </button>
        </div>
      </div>
      <AccordionContent className="border-t" style={{ display: open ? 'block' : 'none' }}>
        <div className="p-4 pb-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-secondary">Remaining ECMEV</p>
              <p className="text-[20px] font-bold leading-[24px] mb-2">
                {graphViewMode === 'percentage' ? `${ecmevChartData[0].value}%` : `€${ecmevRemaining.toLocaleString()}`}
              </p>

              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                  <p className="text-xs text-secondary">Remaining Budget</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#FF2D55]"></span>
                  <p className="text-xs text-secondary">Budget Spent</p>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0">
              <PieGraph
                title="Remaining ECMEV"
                titleValue={
                  graphViewMode === 'percentage' ? `${ecmevChartData[0].value}%` : `€${ecmevRemaining.toLocaleString()}`
                }
                chartConfig={ecmevChartConfig}
                chartData={ecmevChartData}
                className=""
                outerRadius={55}
                minHeight={110}
                hideLabel={true}
              />
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default ECItemAccordion;
